import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabase';

import Navbar from '../components/Navbar';
import CreatePrescription from '../components/CreatePrescription';
import SearchPatient from '../components/SearchPatient';
import PrescriptionHistory from '../components/PrescriptionHistory';
import FitnessCertificate from '../components/FitnessCertificate';
import CertificateViewer from '../components/CertificateViewer';
import PrescriptionViewer from '../components/PrescriptionViewer';
import { toast } from 'sonner';
import {
  LayoutDashboard, FilePlus, Search, History, User, LogOut,
  Menu, X, Calendar, Users, Clock, FileText, FileSignature, 
  FileCheck, ChevronLeft, ChevronRight, Phone, MapPin, CheckCircle2,
  MessageCircle, UserPlus, Bell, ChevronDown, PanelLeftClose, PanelLeftOpen,
  PanelRightClose, PanelRightOpen, Eye, Printer, FileDown, LogIn, Sparkles, ArrowRight, Edit2,
  Activity, Thermometer, Heart, Beaker, AlertTriangle
} from 'lucide-react';

import './DoctorDashboard.css';
import { safeQuery } from '../utils/dbUtils';

const ClinicMedicalIcon = ({ size = 20, className = "" }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 576 512" 
    width={size} 
    height={size} 
    className={className}
  >
    {/* White house base & chimney & roof */}
    <path 
      fill="white" 
      d="M575.8 255.5c0 18-15 32.1-32 32.1h-32l.7 160.2c.2 35.5-28.5 64.3-64 64.3H128.1c-35.3 0-64-28.7-64-64V287.6H32c-18 0-32-14-32-32.1c0-9 3-17 10-24L266.4 8c7-7 15-8 22-8s15 2 21 7L416 100.7V64c0-17.7 14.3-32 32-32h32c17.7 0 32 14.3 32 32V185l52.8 46.4c8 7 12 15 11 24z"
    />
    {/* Blue cross cutout inside the house base */}
    <path 
      fill="#2563eb" 
      d="M272 192c-8.8 0-16 7.2-16 16v48H208c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h48v48c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V320h48c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H320V208c0-8.8-7.2-16-16-16H272z"
    />
  </svg>
);

function DoctorDashboard() {
  const navigate = useNavigate();
  const { logout, currentUser, isRecovering } = useAuth();

  const [selectedPatient, setSelectedPatient] = useState(() => {
    const saved = localStorage.getItem('klinik_context_patient');
    return saved ? JSON.parse(saved) : null;
  });

  const [editPrescription, setEditPrescription] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';
  
  const [showLeaveTabModal, setShowLeaveTabModal] = useState(false);
  const [pendingTab, setPendingTab] = useState(null);

  const setActiveTab = (newTab, force = false) => {
    if (!force && activeTab === 'create-prescription' && newTab !== 'create-prescription') {
      setPendingTab(newTab);
      setShowLeaveTabModal(true);
      return;
    }
    setSearchParams({ tab: newTab });
  };

  const [historyFilter, setHistoryFilter] = useState('all');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [allPrescriptions, setAllPrescriptions] = useState([]);
  const [allCertificates, setAllCertificates] = useState([]);
  const [pendingSearchTerm, setPendingSearchTerm] = useState('');
  const [doctorInfo, setDoctorInfo] = useState(null);
  const [medicineSearchQuery, setMedicineSearchQuery] = useState('');
  const [medicineSearchResults, setMedicineSearchResults] = useState([]);
  const [isSearchingMedicine, setIsSearchingMedicine] = useState(false);
  const [selectedPatientForCert, setSelectedPatientForCert] = useState(() => {
    const saved = localStorage.getItem('klinik_context_cert_patient');
    return saved ? JSON.parse(saved) : null;
  });
  const [viewingCertificate, setViewingCertificate] = useState(null);
  const [appointmentForCert, setAppointmentForCert] = useState(() => {
    const saved = localStorage.getItem('klinik_context_cert_appt');
    return saved ? JSON.parse(saved) : null;
  });
  const [individualPickerAppt, setIndividualPickerAppt] = useState(null);
  
  // Rescheduling States
  const [rescheduleItem, setRescheduleItem] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleBookedSlots, setRescheduleBookedSlots] = useState([]);
  const [selectedRescheduleSlot, setSelectedRescheduleSlot] = useState(null);
  const [isUpdatingReschedule, setIsUpdatingReschedule] = useState(false);


  const searchTimeoutRef = useRef(null);

  const [profileData, setProfileData] = useState({
    name: '',
    phone: '',
    email: '',
    qualification: '',
    specialization: '',
    registration_number: '',
    working_hours: '',
    startTime: '10:00',
    endTime: '20:00',
    available_days: [],
    favorite_medicines: [],
    prescription_templates: [],
    default_followup_days: 0
  });

  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileSubTab, setProfileSubTab] = useState('identity');
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [signatureMode, setSignatureMode] = useState('draw');
  const [signatureFile, setSignatureFile] = useState(null);
  const [isSavingSignature, setIsSavingSignature] = useState(false);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [clinicCapabilities, setClinicCapabilities] = useState({ has_pharmacy: true, has_lab: true });
  const [stats, setStats] = useState({
    appointmentsToday: 0,
    patientsSeen: 0,
    pendingPrescriptions: 0,
    reportsReceived: 0
  });
  const [recentPrescriptions, setRecentPrescriptions] = useState([]);
  const [canvasWidth, setCanvasWidth] = useState(500);
  const sigContainerRef = useRef(null);
  const [appointments, setAppointments] = useState([]);
  const [issuedCertificates, setIssuedCertificates] = useState([]);
  const [appointmentDate, setAppointmentDate] = useState(new Date().toISOString().split('T')[0]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [selectedIndividual, setSelectedIndividual] = useState(null);
  const [waitingQueue, setWaitingQueue] = useState([]);
  const [loadingQueue, setLoadingQueue] = useState(false);

  // Overhaul UX states
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('klinik_doctor_sidebar_collapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(() => {
    const saved = localStorage.getItem('klinik_doctor_right_panel_collapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [patientCountPeriod, setPatientCountPeriod] = useState('today');
  const [patientCounts, setPatientCounts] = useState({ today: 0, weekly: 0, monthly: 0 });
  const [selectedPrescriptionForView, setSelectedPrescriptionForView] = useState(null);
  const [viewMode, setViewMode] = useState('view');
  const [recentLabReports, setRecentLabReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [selectedReportForView, setSelectedReportForView] = useState(null);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [rightPanelFocus, setRightPanelFocus] = useState(() => {
    const saved = localStorage.getItem('klinik_doctor_right_panel_focus');
    return saved || 'appointments';
  });

  // Phone modal states for Start New Prescription
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [modalPhone, setModalPhone] = useState('');
  const [modalSearching, setModalSearching] = useState(false);

  // Persistence hooks for layout states
  useEffect(() => {
    localStorage.setItem('klinik_doctor_sidebar_collapsed', JSON.stringify(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  useEffect(() => {
    localStorage.setItem('klinik_doctor_right_panel_collapsed', JSON.stringify(isRightPanelCollapsed));
  }, [isRightPanelCollapsed]);

  useEffect(() => {
    localStorage.setItem('klinik_doctor_right_panel_focus', rightPanelFocus);
  }, [rightPanelFocus]);



  useEffect(() => {
    if (currentUser) fetchDoctorInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (doctorInfo && activeTab === 'history') {
      fetchAllPrescriptions();
      fetchAllCertificates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorInfo, activeTab]);

  // Context State Persistence Hook
  useEffect(() => {
    if (selectedPatient) localStorage.setItem('klinik_context_patient', JSON.stringify(selectedPatient));
    else localStorage.removeItem('klinik_context_patient');
    
    if (selectedPatientForCert) localStorage.setItem('klinik_context_cert_patient', JSON.stringify(selectedPatientForCert));
    else localStorage.removeItem('klinik_context_cert_patient');
    
    if (appointmentForCert) localStorage.setItem('klinik_context_cert_appt', JSON.stringify(appointmentForCert));
    else localStorage.removeItem('klinik_context_cert_appt');
  }, [selectedPatient, selectedPatientForCert, appointmentForCert]);


  useEffect(() => {
    if (doctorInfo) {
      setProfileData({
        name: doctorInfo.name || '',
        phone: doctorInfo.phone || '',
        email: doctorInfo.email || '',
        qualification: doctorInfo.qualification || '',
        specialization: doctorInfo.specialization || '',
        registration_number: doctorInfo.registration_number || '',
        working_hours: doctorInfo.working_hours || '',
        startTime: (doctorInfo.booking_slots && doctorInfo.booking_slots[0]?.start) || '10:00',
        endTime: (doctorInfo.booking_slots && doctorInfo.booking_slots[0]?.end) || '20:00',
        available_days: doctorInfo.available_days || [],
        favorite_medicines: doctorInfo.favorite_medicines || [],
        prescription_templates: doctorInfo.prescription_templates || [],
        default_followup_days: doctorInfo.default_followup_days || 0
      });

      fetchStats();
      fetchRecentPrescriptions();
      fetchAllCertificates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorInfo]);
 

  useEffect(() => {
    if (activeTab === 'profile' && profileSubTab === 'signature' && sigContainerRef.current) {
      const updateWidth = () => {
        if (sigContainerRef.current) {
          setCanvasWidth(sigContainerRef.current.offsetWidth);
        }
      };
      updateWidth();
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }
  }, [activeTab, profileSubTab]);

  useEffect(() => {
    if (doctorInfo && activeTab === 'appointments') {
      fetchAppointments(appointmentDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorInfo, activeTab, appointmentDate]);

  useEffect(() => {
    if (doctorInfo && activeTab === 'overview') {
      const todayStr = new Date().toISOString().split('T')[0];
      fetchAppointments(todayStr);
    }
  }, [doctorInfo, activeTab]);

  async function fetchDoctorInfo() {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', currentUser.id)
        .single();
      if (error) throw error;
      setDoctorInfo(data);

      if (data.clinic_id) {
        const { data: clinicData } = await supabase
          .from('clinics')
          .select('has_pharmacy, has_lab')
          .eq('id', data.clinic_id)
          .single();
        if (clinicData) {
          setClinicCapabilities({
            has_pharmacy: clinicData.has_pharmacy ?? true,
            has_lab: clinicData.has_lab ?? true
          });
        }
      }
    } catch (error) {
      toast.error('Failed to load doctor profile');
    }
  }

  async function fetchAllPrescriptions() {
    try {
      const { data, error } = await supabase
        .from('prescriptions')
        .select('*')
        .eq('doctor_id', doctorInfo.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setAllPrescriptions(data || []);
    } catch (error) {
      toast.error('Failed to load prescription history');
    }
  }

  async function fetchAllCertificates() {
    if (!doctorInfo) return;
    try {
      const { data, error } = await supabase
        .from('certificates')
        .select('*')
        .eq('doctor_id', doctorInfo.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setAllCertificates(data || []);
    } catch (error) {
      console.error('Failed to load certificates:', error);
    }
  }

  async function fetchRecentLabReports(silent = false) {
    if (!doctorInfo?.id) return;
    if (!silent) setLoadingReports(true);
    try {
      const { data, error } = await supabase
        .from('prescriptions')
        .select('id, patient_name, created_at, updated_at, lab_results, lab_status')
        .eq('doctor_id', doctorInfo.id)
        .eq('lab_status', 'report_uploaded')
        .order('updated_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setRecentLabReports(data || []);
    } catch (err) {
      console.error('Error fetching recent lab reports:', err);
    } finally {
      if (!silent) setLoadingReports(false);
    }
  }

  async function fetchStats(silent = false) {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISO = today.toISOString();

      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      oneWeekAgo.setHours(0, 0, 0, 0);
      const oneWeekAgoISO = oneWeekAgo.toISOString();

      const oneMonthAgo = new Date();
      oneMonthAgo.setDate(oneMonthAgo.getDate() - 30);
      oneMonthAgo.setHours(0, 0, 0, 0);
      const oneMonthAgoISO = oneMonthAgo.toISOString();

      // Fetch all prescriptions in last 30 days
      const { data: rxData, error: rxError } = await supabase
        .from('prescriptions')
        .select('id, status, created_at, lab_status')
        .eq('doctor_id', doctorInfo.id)
        .gte('created_at', oneMonthAgoISO);
      
      if (rxError) throw rxError;

      const allRx = rxData || [];
      const todayRx = allRx.filter(rx => rx.created_at >= todayISO);
      const weeklyRx = allRx.filter(rx => rx.created_at >= oneWeekAgoISO);
      const monthlyRx = allRx;

      setPatientCounts({
        today: todayRx.length,
        weekly: weeklyRx.length,
        monthly: monthlyRx.length
      });

      setStats({
        appointmentsToday: 0,
        patientsSeen: todayRx.length,
        pendingPrescriptions: todayRx.filter(rx => rx.status === 'pending').length,
        reportsReceived: todayRx.filter(rx => rx.lab_status === 'report_uploaded').length
      });

      // Fetch today's appointment count
      const todayStr = new Date().toISOString().split('T')[0];
      const { data: apptData } = await supabase
        .from('appointments')
        .select('id')
        .eq('doctor_id', doctorInfo.id)
        .eq('preferred_date', todayStr)
        .neq('status', 'cancelled');
      if (apptData) {
        setStats(prev => ({ ...prev, appointmentsToday: apptData.length }));
      }

      fetchRecentLabReports(silent);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }

  async function fetchRecentPrescriptions() {
    try {
      const { data, error } = await supabase
        .from('prescriptions')
        .select('*')
        .eq('doctor_id', doctorInfo.id)
        .order('created_at', { ascending: false })
        .limit(5);
      if (error) throw error;
      setRecentPrescriptions(data || []);
    } catch (error) {
      console.error('Error fetching prescriptions:', error);
    }
  }

  async function fetchWaitingQueue(silent = false) {
    if (!doctorInfo?.clinic_id) return;
    if (!silent) setLoadingQueue(true);
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISO = today.toISOString();

      const { data, error } = await supabase
        .from('prescriptions')
        .select('*')
        .eq('clinic_id', doctorInfo.clinic_id)
        .eq('status', 'checked_in')
        .gte('created_at', todayISO)
        .order('token_number', { ascending: true });

      if (error) throw error;
      setWaitingQueue(data || []);
    } catch (err) {
      console.error('Error fetching waiting queue:', err);
    } finally {
      if (!silent) setLoadingQueue(false);
    }
  }

  useEffect(() => {
    if ((activeTab === 'queue' || activeTab === 'overview') && doctorInfo?.clinic_id) {
      fetchWaitingQueue();
    }
  }, [activeTab, doctorInfo]);

  // Real-time Subscriptions
  useEffect(() => {
    if (!doctorInfo?.clinic_id) return;

    const channel = supabase.channel('doctor_dashboard_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'prescriptions' }, () => {
        fetchRecentPrescriptions();
        fetchRecentLabReports(true);
        fetchStats(true);
        fetchWaitingQueue(true);
        if (activeTab === 'history') fetchAllPrescriptions();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'appointments' }, () => {
        fetchAppointments(appointmentDate, true);
        fetchStats(true);
        fetchWaitingQueue(true);
      })
      .subscribe();

    // Tab Focus trigger - lightweight safety net
    const handleFocus = () => {
      fetchWaitingQueue(true);
      fetchRecentPrescriptions();
      fetchRecentLabReports(true);
      fetchStats(true);
      fetchAppointments(appointmentDate, true);
      if (activeTab === 'history') fetchAllPrescriptions();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener('focus', handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorInfo, activeTab, appointmentDate]);

  async function handleLogout() {
    await logout();
    navigate('/');
  }

  async function fetchAppointments(date, silent = false) {
    if (!silent) setLoadingAppointments(true);
    try {
      // 1. Fetch Appointments
      const apptResult = await safeQuery(
        supabase
          .from('appointments')
          .select('*')
          .eq('doctor_id', doctorInfo.id)
          .eq('preferred_date', date)
          .neq('status', 'cancelled')
          .order('slot_time', { ascending: true }),
        { label: 'Fetch Appointments', showToast: false }
      );
      
      if (apptResult.error) throw apptResult.error;
      setAppointments(apptResult.data || []);

      // 2. Fetch Certificates issued for this date to track individual completion
      const certResult = await safeQuery(
        supabase
          .from('certificates')
          .select('id, patient_name, appointment_id, certificate_type, created_at')
          .eq('doctor_id', doctorInfo.id)
          .gte('created_at', date + 'T00:00:00')
          .lte('created_at', date + 'T23:59:59'),
        { label: 'Fetch Status Tracking', showToast: false }
      );

      if (!certResult.error) {
        setIssuedCertificates(certResult.data || []);
      }
    } catch (error) {
      toast.error('Failed to load appointments');
    } finally {
      if (!silent) setLoadingAppointments(false);
    }
  }


  async function updateAppointmentStatus(appointmentId, newStatus) {
    try {
      const { error } = await supabase
        .from('appointments')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', appointmentId);
      if (error) throw error;
      toast.success(`Appointment ${newStatus}`);
      fetchAppointments(appointmentDate);
      fetchStats();
    } catch (error) {
      toast.error('Failed to update appointment');
    }
  }

  function formatApptTime(slot) {
    if (!slot) return '';
    const [h, m] = slot.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hr = h % 12 || 12;
    return `${hr}:${String(m).padStart(2, '0')} ${ampm}`;
  }

  function formatApptDate(dateStr) {
    const [y, mo, d] = dateStr.split('-').map(Number);
    return new Date(y, mo - 1, d).toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' });
  }

  function shiftDate(offset) {
    const [y, m, d] = appointmentDate.split('-').map(Number);
    const dt = new Date(y, m - 1, d + offset);
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    setAppointmentDate(`${yyyy}-${mm}-${dd}`);
  }

  function handleStartCertFromAppointment(appointment) {
    const individuals = appointment.individuals || [];
    if (individuals.length === 0) {
      toast.error('No individual data found in this appointment');
      return;
    }

    // Always show the picker so that "+ Add Person (Not in list)" is available
    if (individualPickerAppt?.id === appointment.id) {
      setIndividualPickerAppt(null);
    } else {
      setIndividualPickerAppt(appointment);
    }
  }

  function selectIndividualForCert(appointment, individual) {
    // 1. Lock in the data context
    setAppointmentForCert(appointment);
    setSelectedIndividual(individual);
    setSelectedPatientForCert({
      name: individual.name,
      age: individual.age,
      gender: individual.gender,
      phone: appointment.patient_phone
    });

    // 2. Initiate navigation immediately
    setActiveTab('certificate');

    // 3. Clear picker after a short delay or alongside tab change
    setTimeout(() => {
      setIndividualPickerAppt(null);
    }, 50);
  }

  // Rescheduling Logic
  const handleOpenReschedule = async (appt) => {
    setRescheduleItem(appt);
    setRescheduleDate(appt.preferred_date);
    setSelectedRescheduleSlot(null);
    await fetchRescheduleBookedSlots(appt.preferred_date);
  };

  const fetchRescheduleBookedSlots = async (date) => {
    try {
      const { data, error } = await supabase
        .from('appointments')
        .select('slot_time')
        .eq('preferred_date', date)
        .neq('status', 'cancelled');
      
      if (!error && data) {
        setRescheduleBookedSlots(data.map(d => d.slot_time));
      }
    } catch (err) {
      console.error('Error fetching booked slots:', err);
    }
  };

  const handleUpdateSchedule = async () => {
    if (!rescheduleItem || !rescheduleDate || !selectedRescheduleSlot) return;

    setIsUpdatingReschedule(true);
    try {
      const { error } = await supabase
        .from('appointments')
        .update({
          preferred_date: rescheduleDate,
          slot_time: selectedRescheduleSlot
        })
        .eq('id', rescheduleItem.id);

      if (error) throw error;

      toast.success('Appointment rescheduled successfully');
      setRescheduleItem(null);
      setSelectedRescheduleSlot(null);
      
      // CRITICAL: Refresh the list with the ACTIVE date
      fetchAppointments(appointmentDate);
      fetchStats();
    } catch (err) {
      console.error('Reschedule error:', err);
      toast.error('Failed to reschedule: ' + err.message);
    } finally {
      setIsUpdatingReschedule(false);
    }
  };

  const generateDashboardTimeSlots = () => {
    if (!doctorInfo?.booking_slots) return [];
    let slotsConfig = doctorInfo.booking_slots;
    if (typeof slotsConfig === 'string') {
      try { slotsConfig = JSON.parse(slotsConfig); } catch (e) { return []; }
    }
    if (!Array.isArray(slotsConfig)) return [];

    const slots = [];
    slotsConfig.forEach(range => {
      let current = range.start;
      while (current < range.end) {
        slots.push(current);
        const [h, m] = current.split(':').map(Number);
        let nh = h, nm = m + 30;
        if (nm >= 60) { nm = 0; nh += 1; }
        current = `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
      }
    });
    return slots;
  };


  async function handleSaveProfile(e) {
    e.preventDefault();
    setIsSavingProfile(true);
    try {
      const { data, error } = await supabase
        .from('users')
        .update({
          name: profileData.name,
          phone: profileData.phone,
          qualification: profileData.qualification,
          specialization: profileData.specialization,
          registration_number: profileData.registration_number,
          working_hours: `${profileData.startTime} - ${profileData.endTime}`,
          booking_slots: [{ start: profileData.startTime, end: profileData.endTime }],
          available_days: profileData.available_days,
          favorite_medicines: profileData.favorite_medicines,
          prescription_templates: profileData.prescription_templates,
          default_followup_days: parseInt(profileData.default_followup_days) || 0
        })

        .eq('id', currentUser.id)
        .select();

      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error('Supabase blocked the update. Check row-level security (RLS) policies for UPDATE on the "users" table.');
      }

      toast.success('Profile updated successfully');
      setIsEditingProfile(false);
      
      // Refresh global doctorInfo so sidebar updates instantly
      fetchDoctorInfo();
    } catch (err) {
      toast.error('Failed to update profile: ' + err.message);
    } finally {
      setIsSavingProfile(false);
    }
  }

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    // Scale coordinates if css size differs from element resolution
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000';
    ctx.beginPath();
    ctx.moveTo((clientX - rect.left) * scaleX, (clientY - rect.top) * scaleY);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault(); // prevent scroll jumping on mobile touch
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    ctx.lineTo((clientX - rect.left) * scaleX, (clientY - rect.top) * scaleY);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignaturePad = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const dataURItoBlob = (dataURI) => {
    const byteString = atob(dataURI.split(',')[1]);
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], {type: mimeString});
  };

  async function handleSaveSignature() {
    setIsSavingSignature(true);
    try {
      let fileToUpload = null;
      let fileExt = 'png';

      if (signatureMode === 'upload' && signatureFile) {
        fileToUpload = signatureFile;
        fileExt = signatureFile.name.split('.').pop();
      } else if (signatureMode === 'draw' && canvasRef.current) {
        const dataUrl = canvasRef.current.toDataURL('image/png');
        // Simple heuristic: if context hasn't been drawn significantly, warn empty.
        // We skip exact empty checking for simplicity right now.
        fileToUpload = dataURItoBlob(dataUrl);
      } else {
        throw new Error('Please draw or select a signature file.');
      }

      const fileName = `dr_${currentUser.id}_${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('signatures')
        .upload(fileName, fileToUpload, { cacheControl: '3600', upsert: false });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('signatures')
        .getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from('users')
        .update({ signature_url: publicUrl })
        .eq('id', currentUser.id);

      if (updateError) throw updateError;

      toast.success('Signature saved successfully');
      setSignatureFile(null);
      if (signatureMode === 'draw') clearSignaturePad();
      fetchDoctorInfo();
    } catch (err) {
      toast.error('Failed to save signature: ' + err.message);
    } finally {
      setIsSavingSignature(false);
    }
  }
  
  async function handleRemoveSignature() {
    try {
      const { error } = await supabase
        .from('users')
        .update({ signature_url: null })
        .eq('id', currentUser.id);
      if (error) throw error;
      toast.success('Signature removed');
      fetchDoctorInfo();
    } catch (err) {
      toast.error('Failed to remove signature: ' + err.message);
    }
  }

  const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const handleMedicineSearch = async (query) => {
    setMedicineSearchQuery(query);
    if (!query.trim()) {
      setMedicineSearchResults([]);
      return;
    }
    
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    
    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearchingMedicine(true);
      try {
        const { data, error } = await supabase.rpc('search_medicines_fuzzy', {
          search_query: query,
          max_results: 10
        });
        if (error) throw error;
        setMedicineSearchResults(data || []);
      } catch (err) {
        console.error('Error searching medicines:', err);
      } finally {
        setIsSearchingMedicine(false);
      }
    }, 300);
  };

  const addFavoriteMedicine = (medicine) => {
    const currentFavs = profileData.favorite_medicines || [];
    if (!currentFavs.some(f => f.name.toLowerCase() === medicine.name.toLowerCase())) {
      const newFav = { name: medicine.name, composition: medicine.composition || '' };
      setProfileData({ ...profileData, favorite_medicines: [...currentFavs, newFav] });
    }
    setMedicineSearchQuery('');
    setMedicineSearchResults([]);
  };

  const removeFavoriteMedicine = (medName) => {
    const currentFavs = profileData.favorite_medicines || [];
    setProfileData({ ...profileData, favorite_medicines: currentFavs.filter(f => f.name !== medName) });
  };

  const removeTemplate = (templateName) => {
    const currentTpls = profileData.prescription_templates || [];
    setProfileData({ ...profileData, prescription_templates: currentTpls.filter(t => t.template_name !== templateName) });
  };

  const toggleDay = (day) => {
    const currentDays = profileData.available_days || [];
    if (currentDays.includes(day)) {
      setProfileData({ ...profileData, available_days: currentDays.filter(d => d !== day) });
    } else {
      // Keep them sorted
      const newDays = [...currentDays, day].sort((a, b) => WEEKDAYS.indexOf(a) - WEEKDAYS.indexOf(b));
      setProfileData({ ...profileData, available_days: newDays });
    }
  };

  function handlePrescriptionComplete() {
    setSelectedPatient(null);
    setEditPrescription(null);
    setActiveTab('overview', true);
    toast.success('Prescription saved successfully');
    fetchStats();
    fetchRecentPrescriptions();
  }

  function handleSelectPatient(patient) {
    setSelectedPatient(patient);
    setEditPrescription(null);
    setActiveTab('create-prescription');
  }

  function handleEditPrescription(prescription) {
    if (prescription.pharmacy_status === 'completed' || prescription.lab_status === 'completed' || prescription.status === 'dispensed') {
      toast.error('Cannot edit: Pharmacy or Lab has already completed this prescription.');
      return;
    }
    setEditPrescription(prescription);
    setSelectedPatient(null);
    setActiveTab('create-prescription');
  }

  async function proceedWithCancel(prescription) {
    try {
      const { error } = await supabase
        .from('prescriptions')
        .update({
          status: 'cancelled',
          pharmacy_status: 'not_applicable',
          lab_status: 'not_applicable',
          sent_to_pharmacy: false,
          sent_to_lab: false
        })
        .eq('id', prescription.id);

      if (error) throw error;
      toast.success('Prescription cancelled successfully');

      if (activeTab === 'history') {
        fetchAllPrescriptions();
      }
      fetchStats();
      fetchRecentPrescriptions();
    } catch (error) {
      toast.error('Failed to cancel prescription: ' + error.message);
    }
  }

  function handleCancelPrescription(prescription) {
    if (prescription.pharmacy_status === 'completed' || prescription.lab_status === 'completed' || prescription.status === 'dispensed') {
      toast.error('Cannot cancel: Pharmacy or Lab has already completed this prescription.');
      return;
    }

    toast('Cancel Prescription?', {
      description: 'Are you sure you want to cancel/revoke this prescription? This will remove it from the Pharmacy and Lab queues.',
      action: {
        label: 'Yes, Cancel',
        onClick: () => proceedWithCancel(prescription)
      },
      cancel: {
        label: 'No, Keep'
      },
      duration: 8000
    });
  }

  async function handleProceedWithPhone() {
    if (modalPhone.length < 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    setModalSearching(true);
    try {
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .eq('clinic_id', doctorInfo.clinic_id)
        .eq('phone', modalPhone)
        .limit(1);

      if (error) throw error;

      if (data && data.length > 0) {
        // Patient found! Select them and transition
        setSelectedPatient(data[0]);
        localStorage.setItem('klinik_context_patient', JSON.stringify(data[0]));
        setEditPrescription(null);
        setActiveTab('create-prescription');
        setShowPhoneModal(false);
        setModalPhone('');
        toast.success(`Loaded profile for ${data[0].name}`);
      } else {
        // Patient not found! Ask if they want to create
        toast.error('Patient profile not found for this number');
        // Switch to search/add tab and pre-fill search term
        setPendingSearchTerm(modalPhone);
        setActiveTab('search-patient');
        setShowPhoneModal(false);
        setModalPhone('');
      }
    } catch (err) {
      toast.error('Error finding patient: ' + err.message);
    } finally {
      setModalSearching(false);
    }
  }

  async function handleStartConsultation(prescription) {
    if (!doctorInfo) {
      toast.error('Doctor profile is not loaded yet. Please wait.');
      return;
    }

    try {
      // 1. Update the prescription row in database
      const { data: updatedPrescription, error: updateErr } = await supabase
        .from('prescriptions')
        .update({
          doctor_id: doctorInfo.id,
          doctor_name: doctorInfo.name,
          doctor_qualification: doctorInfo.qualification || null,
          doctor_specialization: doctorInfo.specialization || null,
          status: 'in_progress',
          updated_at: new Date().toISOString()
        })
        .eq('id', prescription.id)
        .select()
        .single();

      if (updateErr) throw updateErr;

      // 2. Load the full patient record from the patients table
      const { data: patientData, error: patientErr } = await supabase
        .from('patients')
        .select('*')
        .eq('id', prescription.patient_id)
        .single();

      if (patientErr) throw patientErr;

      // 3. Attach full patient data to prescription to prevent redundant load
      const finalPrescription = {
        ...updatedPrescription,
        patients: patientData
      };

      // 4. Load the prescription into edit state and switch tabs
      setEditPrescription(finalPrescription);
      setSelectedPatient(null);
      setActiveTab('create-prescription');

      toast.success('Consultation started successfully!');
    } catch (err) {
      console.error('Error starting consultation:', err);
      toast.error('Failed to start consultation: ' + err.message);
    }
  }

  const getGreeting = () => {
    const hr = new Date().getHours();
    if (hr < 12) return 'Good morning';
    if (hr < 17) return 'Good afternoon';
    if (hr < 21) return 'Good evening';
    return 'Good night';
  };

  const getWeekDates = () => {
    const week = [];
    const today = new Date();
    // Monday is the start of current week
    const dayOfWeek = today.getDay();
    const distanceToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + distanceToMonday);

    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      
      const yearStr = date.getFullYear();
      const monthStr = String(date.getMonth() + 1).padStart(2, '0');
      const dayStr = String(date.getDate()).padStart(2, '0');
      const dateString = `${yearStr}-${monthStr}-${dayStr}`;

      week.push({
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 2).toUpperCase(),
        dayNum: date.getDate(),
        dateString,
        isToday: today.getDate() === date.getDate() && today.getMonth() === date.getMonth() && today.getFullYear() === date.getFullYear()
      });
    }
    return week;
  };

  if (!doctorInfo) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--gray-50)' }}>
        <div className="spinner"></div> Loading...
      </div>
    );
  }

  const greeting = getGreeting();

  return (
    <>
      <AnimatePresence>
        {isRecovering && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="recovery-overlay"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="recovery-card"
            >
              <div className="pulse-container">
                <div className="pulse-circle"></div>
                <Clock className="pulse-icon" size={32} />
              </div>
              <h2>Securing Connection</h2>
              <p>Re-synchronizing your clinic session. Almost there...</p>
              <div className="discovery-loader"></div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className={`doctor-layout ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {mobileMenuOpen && (
          <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)}></div>
        )}

        <aside className={`doctor-sidebar ${isSidebarCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="sidebar-logo-section">
            <div className="logo-badge" style={{ backgroundColor: '#2563eb', borderRadius: '50%', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'none' }}>
              <ClinicMedicalIcon size={20} />
            </div>
            {!isSidebarCollapsed && <span className="sidebar-logo-text" style={{ color: '#2563eb', fontWeight: 800 }}>Klinik</span>}
            {!isSidebarCollapsed && (
              <button className="sidebar-toggle-btn" onClick={() => setIsSidebarCollapsed(true)}>
                <PanelLeftClose size={18} />
              </button>
            )}
          </div>

          <nav className="sidebar-nav">
            <button 
              className={`nav-item ${activeTab === 'overview' ? 'active' : ''}`} 
              onClick={() => { setActiveTab('overview'); setMobileMenuOpen(false); }}
              title={isSidebarCollapsed ? "Dashboard" : ""}
            >
              <LayoutDashboard size={20} />
              {!isSidebarCollapsed && <span>Dashboard</span>}
            </button>
            <button 
              className={`nav-item ${activeTab === 'queue' ? 'active' : ''}`} 
              onClick={() => { setActiveTab('queue'); setMobileMenuOpen(false); }}
              title={isSidebarCollapsed ? "Patient Queue" : ""}
            >
              <Users size={20} />
              {!isSidebarCollapsed && <span>Patient Queue</span>}
            </button>
            <button 
              className={`nav-item ${activeTab === 'search-patient' ? 'active' : ''}`} 
              onClick={() => { setActiveTab('search-patient'); setMobileMenuOpen(false); }}
              title={isSidebarCollapsed ? "Search Patients" : ""}
            >
              <Search size={20} />
              {!isSidebarCollapsed && <span>Search Patients</span>}
            </button>
            <button 
              className={`nav-item ${activeTab === 'appointments' ? 'active' : ''}`} 
              onClick={() => { setActiveTab('appointments'); setMobileMenuOpen(false); }}
              title={isSidebarCollapsed ? "Appointments" : ""}
            >
              <Calendar size={20} />
              {!isSidebarCollapsed && <span>Appointments</span>}
            </button>
            <button 
              className={`nav-item ${activeTab === 'history' ? 'active' : ''}`} 
              onClick={() => { setActiveTab('history'); setHistoryFilter('all'); setMobileMenuOpen(false); }}
              title={isSidebarCollapsed ? "Prescription History" : ""}
            >
              <History size={20} />
              {!isSidebarCollapsed && <span>Prescription History</span>}
            </button>
            <button 
              className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`} 
              onClick={() => { setActiveTab('profile'); setMobileMenuOpen(false); }}
              title={isSidebarCollapsed ? "My Profile" : ""}
            >
              <User size={20} />
              {!isSidebarCollapsed && <span>My Profile</span>}
            </button>
          </nav>

          <div className="sidebar-footer">
            <button className="logout-btn-sidebar" onClick={handleLogout} title={isSidebarCollapsed ? "Logout" : ""}>
              <LogOut size={18} />
              {!isSidebarCollapsed && <span>Logout</span>}
            </button>
          </div>
        </aside>

        <main className="doctor-main">
          <header className="doctor-topbar">
            <div className="topbar-left">
              <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                <Menu size={22} />
              </button>
              {isSidebarCollapsed && (
                <button className="desktop-expand-btn" onClick={() => setIsSidebarCollapsed(false)}>
                  <PanelLeftOpen size={20} />
                </button>
              )}
              <h1 className="topbar-title">
                {activeTab === 'overview' && `${greeting}, Dr. ${doctorInfo?.name?.split(' ')?.pop() || 'Doctor'}`}
                {activeTab === 'queue' && 'Patient Queue'}
                {activeTab === 'create-prescription' && 'New Prescription'}
                {activeTab === 'search-patient' && 'Search Patients'}
                {activeTab === 'appointments' && 'Appointments'}
                {activeTab === 'history' && 'Prescription History'}
                {activeTab === 'profile' && 'My Profile'}
              </h1>
            </div>

            <div className="topbar-right">
              <button className="notification-btn" title="Notifications">
                <Bell size={20} />
                <span className="notification-badge" />
              </button>

              <div className="profile-dropdown-container">
                <button className="profile-dropdown-trigger" onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}>
                  <div className="avatar-circle">
                    {doctorInfo?.name?.charAt(0).toUpperCase() || 'D'}
                  </div>
                  <span className="profile-name">Dr. {doctorInfo?.name?.split(' ')?.pop() || 'Doctor'}</span>
                  <ChevronDown size={16} className={`chevron-icon ${profileDropdownOpen ? 'open' : ''}`} />
                </button>

                {profileDropdownOpen && (
                  <>
                    <div className="dropdown-backdrop" onClick={() => setProfileDropdownOpen(false)} />
                    <div className="dropdown-menu">
                      <div className="dropdown-header">
                        <h4>{doctorInfo?.name}</h4>
                        <p>{doctorInfo?.specialization}</p>
                      </div>
                      <button className="dropdown-item" onClick={() => { setActiveTab('profile'); setProfileDropdownOpen(false); }}>
                        <User size={16} />
                        <span>My Profile</span>
                      </button>
                      <button className="dropdown-item logout" onClick={() => { handleLogout(); setProfileDropdownOpen(false); }}>
                        <LogOut size={16} />
                        <span>Logout</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </header>

          <div className="doctor-content">
            {activeTab === 'queue' && (
              <div className="queue-section">
                {/* Queue Page Header */}
                <div className="queue-page-header">
                  <h2 className="queue-page-title">Patient Queue</h2>
                  <p className="queue-page-subtitle">
                    {waitingQueue.length > 0 
                      ? `${waitingQueue.length} patient${waitingQueue.length > 1 ? 's' : ''} waiting`
                      : 'No patients in queue right now'}
                  </p>
                </div>

                {loadingQueue && waitingQueue.length === 0 ? (
                  <div className="queue-loading-state">
                    <div className="queue-spinner"></div>
                    <p>Loading queue...</p>
                  </div>
                ) : waitingQueue.length === 0 ? (
                  <div className="queue-empty-state">
                    <div className="queue-empty-icon-wrap">
                      <CheckCircle2 size={32} />
                    </div>
                    <h3>All caught up!</h3>
                    <p>No patients are waiting. New check-ins will appear here automatically.</p>
                  </div>
                ) : (
                  <div className="queue-grid">
                    {waitingQueue.map((item, index) => {
                      const vitals = item.vitals || {};
                      const hasVitals = vitals.bp || vitals.blood_pressure || vitals.spo2 || vitals.pulse || vitals.temperature;
                      const waitMinutes = Math.round((Date.now() - new Date(item.created_at).getTime()) / 60000);
                      const checkedInTime = new Date(item.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                      return (
                        <div key={item.id} className={`queue-card ${index === 0 ? 'next-up' : ''}`}>
                          <div className="queue-card-header">
                            <div className="queue-patient-row">
                              <div className="queue-avatar">
                                {item.patient_name?.charAt(0).toUpperCase()}
                              </div>
                              <div className="patient-info">
                                <h3>{item.patient_name}</h3>
                                <p>{item.patient_gender} • {item.patient_age} yrs</p>
                              </div>
                            </div>
                            <span className="token-badge">#{item.token_number}</span>
                          </div>

                          <div className="queue-meta-row">
                            <span className="queue-meta-chip">
                              <Clock size={12} />
                              {checkedInTime}
                            </span>
                            <span className={`queue-wait-chip ${waitMinutes > 30 ? 'long' : waitMinutes > 15 ? 'medium' : 'short'}`}>
                              {waitMinutes > 0 ? `${waitMinutes}m wait` : 'Just now'}
                            </span>
                          </div>

                          {/* Reason for Visit Section */}
                          {vitals.visit_reason && (
                            <div className="queue-card-section border-top">
                              <span className="queue-section-label">Reason for Visit</span>
                              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155', lineHeight: 1.4 }}>
                                {vitals.visit_reason}
                              </div>
                            </div>
                          )}

                          {/* Tests Section */}
                          {item.tests && item.tests.length > 0 && (
                            <div className="queue-card-section border-top">
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                <span className="queue-section-label" style={{ margin: 0 }}>Prescribed Tests</span>
                                <span className={`status-badge ${item.lab_status || 'pending'}`} style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem', textTransform: 'capitalize' }}>
                                  {item.lab_status ? item.lab_status.replace('_', ' ') : 'Pending'}
                                </span>
                              </div>
                              <div className="tests-tags">
                                {item.tests.map((test, idx) => (
                                  <span key={idx} className="test-tag-mini">
                                    <Beaker size={10} style={{ marginRight: '4px', flexShrink: 0 }} />
                                    {test}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          <button 
                            onClick={() => handleStartConsultation(item)}
                            className="btn-start-consult"
                          >
                            Start Consultation
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {activeTab === 'overview' && (
              <div className="dashboard-grid">
                {/* Main panel of dashboard */}
                <div className="dashboard-main-content">
                  {/* Greeting & Period Selector Row */}
                  <div className="dashboard-welcome-header">
                    <div className="welcome-message-group">
                      <h2>{greeting}, Dr. {doctorInfo?.name?.split(' ')?.pop() || 'Doctor'}</h2>
                    </div>
                    <div className="period-toggle-pill">
                      <button className={patientCountPeriod === 'today' ? 'active' : ''} onClick={() => setPatientCountPeriod('today')}>Today</button>
                      <button className={patientCountPeriod === 'week' ? 'active' : ''} onClick={() => setPatientCountPeriod('week')}>Week</button>
                      <button className={patientCountPeriod === 'month' ? 'active' : ''} onClick={() => setPatientCountPeriod('month')}>Month</button>
                    </div>
                  </div>

                  {/* Horizontal Square Cards Row */}
                  <div className="dashboard-cards-row">
                    {/* Square 1: Total Patients */}
                    <div className="dashboard-square-card stats-card">
                      <div className="card-top-icon-group">
                        <div className="card-icon-circle">
                          <Calendar size={18} />
                        </div>
                      </div>
                      
                      <div className="stats-metric-display">
                        <span className="metric-value">
                          {patientCountPeriod === 'today' && patientCounts.today}
                          {patientCountPeriod === 'week' && patientCounts.weekly}
                          {patientCountPeriod === 'month' && patientCounts.monthly}
                        </span>
                        <span className="metric-trend-badge positive">
                          {patientCountPeriod === 'today' && '↑ 2% today'}
                          {patientCountPeriod === 'week' && '↑ 8% week'}
                          {patientCountPeriod === 'month' && '↑ 14% month'}
                        </span>
                      </div>
                      <p className="metric-desc">Total patients</p>
                    </div>

                    {/* Square 2: Start New Prescription */}
                    <div 
                      className="dashboard-square-card action-card green"
                      onClick={() => setShowPhoneModal(true)}
                    >
                      <div className="action-card-icon-wrap">
                        <FilePlus size={18} />
                      </div>
                      <div className="action-card-text">
                        <h3>Start new prescription</h3>
                      </div>
                      <button className="action-card-circle-btn">
                        <ChevronRight size={18} />
                      </button>
                    </div>

                    {/* Square 3: Next Patient in Queue */}
                    {waitingQueue.length > 0 ? (
                      <div 
                        className="dashboard-square-card action-card purple"
                        onClick={() => handleStartConsultation(waitingQueue[0])}
                      >
                        <div className="action-card-avatar-wrap">
                          <div className="avatar-letter">
                            {waitingQueue[0].patient_name?.charAt(0).toUpperCase()}
                          </div>
                        </div>
                        <div className="action-card-text">
                          <h3>{waitingQueue[0].patient_name}</h3>
                          {waitingQueue[0].vitals?.visit_reason ? (
                            <p style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                              Reason: {waitingQueue[0].vitals.visit_reason}
                            </p>
                          ) : (
                            <p>Next patient in queue</p>
                          )}
                          <p style={{ opacity: 0.7, fontSize: '0.75rem', marginTop: '0.2rem' }}>
                            {waitingQueue.length - 1 > 0 
                              ? `+ ${waitingQueue.length - 1} more patient${waitingQueue.length - 1 > 1 ? 's' : ''} in queue` 
                              : 'Last patient in queue'}
                          </p>
                        </div>
                        <button className="action-card-circle-btn">
                          <ChevronRight size={18} />
                        </button>
                      </div>
                    ) : (
                      <div className="dashboard-square-card action-card purple empty">
                        <div className="action-card-icon-wrap">
                          <CheckCircle2 size={18} />
                        </div>
                        <div className="action-card-text">
                          <h3>All caught up!</h3>
                          <p>No patients waiting in queue</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Recent Prescriptions Table */}
                  <div className="overhauled-table-section">
                    <div className="table-section-header">
                      <h2>Recent Prescriptions</h2>
                      <button className="view-all-link" onClick={() => { setActiveTab('history'); setHistoryFilter('all'); }}>
                        View All History
                      </button>
                    </div>

                    {recentPrescriptions.length === 0 ? (
                      <div className="empty-state-theme">
                        <FileSignature size={48} className="empty-icon" />
                        <h3>No prescriptions recorded</h3>
                        <p>Prescriptions will appear here once consultations are completed.</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="custom-dashboard-table">
                          <thead>
                            <tr>
                              <th>Patient</th>
                              <th>Date</th>
                              <th>Status</th>
                              <th>Lab Result</th>
                              <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {recentPrescriptions.slice(0, 5).map(prescription => {
                              const isReportUploaded = prescription.lab_status === 'report_uploaded';
                              return (
                                <tr key={prescription.id}>
                                  <td className="patient-cell">
                                    <div className="p-avatar">
                                      {prescription.patient_name?.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="p-info">
                                      <span className="p-name">{prescription.patient_name}</span>
                                      <span className="p-meta">{prescription.patient_gender} • {prescription.patient_age}y</span>
                                    </div>
                                  </td>
                                  <td>{new Date(prescription.created_at).toLocaleDateString()}</td>
                                  <td>
                                    <span className={`status-badge-mini ${prescription.status}`}>
                                      {prescription.status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                    </span>
                                  </td>
                                  <td>
                                    {isReportUploaded ? (
                                      <a 
                                        className="lab-report-uploaded-btn"
                                        href={`${window.location.origin}/app/report/${prescription.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="Click to view lab report"
                                        style={{ display: 'inline-block', textDecoration: 'none', textAlign: 'center' }}
                                      >
                                        View Report
                                      </a>
                                    ) : (
                                      <span className="lab-status-na">Not applicable</span>
                                    )}
                                  </td>
                                  <td style={{ textAlign: 'right' }}>
                                    <div className="action-buttons-group">
                                      {['draft', 'in_progress'].includes(prescription.status) ? (
                                        <button 
                                          className="action-btn-text" 
                                          title="Continue Prescription"
                                          onClick={() => handleEditPrescription(prescription)}
                                        >
                                          Continue
                                        </button>
                                      ) : (
                                        <button 
                                          className="action-btn-icon" 
                                          title="Edit Prescription"
                                          onClick={() => handleEditPrescription(prescription)}
                                        >
                                          <Edit2 size={16} />
                                        </button>
                                      )}
                                      <button 
                                        className="action-btn-icon" 
                                        title="View Prescription"
                                        onClick={() => {
                                          setSelectedPrescriptionForView(prescription);
                                          setViewMode('view');
                                        }}
                                      >
                                        <Eye size={16} />
                                      </button>
                                      <button 
                                        className="action-btn-icon" 
                                        title="Print Prescription"
                                        onClick={() => {
                                          setSelectedPrescriptionForView(prescription);
                                          setViewMode('print');
                                        }}
                                      >
                                        <Printer size={16} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>

                {/* Overhauled Right Panel */}
                <div className={`dashboard-right-panel ${isRightPanelCollapsed ? 'collapsed' : ''}`}>
                  <div className="right-panel-header" style={{ justifyContent: 'flex-end', paddingBottom: 0 }}>
                    <button className="panel-collapse-btn" onClick={() => setIsRightPanelCollapsed(!isRightPanelCollapsed)}>
                      {isRightPanelCollapsed ? <PanelRightOpen size={18} /> : <PanelRightClose size={18} />}
                    </button>
                  </div>

                  <div className="right-panel-content">
                    {/* Appointments / Reports Switcher Tab */}
                    <div className="right-panel-tabs">
                      <button 
                        className={`panel-tab-btn ${rightPanelFocus === 'appointments' ? 'active' : ''}`}
                        onClick={() => setRightPanelFocus('appointments')}
                      >
                        Appointments
                      </button>
                      <button 
                        className={`panel-tab-btn ${rightPanelFocus === 'reports' ? 'active' : ''}`}
                        onClick={() => setRightPanelFocus('reports')}
                      >
                        Lab Reports
                      </button>
                    </div>

                    {rightPanelFocus === 'appointments' ? (
                      <div className="right-panel-section top-half" style={{ flex: 1, borderBottom: 'none' }}>
                        <div className="calendar-month-selector">
                          <h4>{new Date(appointmentDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h4>
                          <div className="calendar-nav-arrows">
                            <button 
                              className="nav-arrow-btn" 
                              title="Previous Day"
                              onClick={() => {
                                const prev = new Date(appointmentDate);
                                prev.setDate(prev.getDate() - 1);
                                setAppointmentDate(prev.toISOString().split('T')[0]);
                              }}
                            >
                              <ChevronLeft size={14} />
                            </button>
                            <button 
                              className="nav-arrow-btn" 
                              title="Next Day"
                              onClick={() => {
                                const next = new Date(appointmentDate);
                                next.setDate(next.getDate() + 1);
                                setAppointmentDate(next.toISOString().split('T')[0]);
                              }}
                            >
                              <ChevronRight size={14} />
                            </button>
                          </div>
                        </div>

                        {/* Weekday slider strip */}
                        <div className="weekday-slider-strip">
                          {getWeekDates().map((day, idx) => (
                            <div 
                              key={idx} 
                              className={`weekday-slider-item ${appointmentDate === day.dateString ? 'active' : ''}`}
                              onClick={() => setAppointmentDate(day.dateString)}
                              style={{ cursor: 'pointer' }}
                            >
                              <span className="weekday-name">{day.dayName}</span>
                              <span className="weekday-number">{day.dayNum}</span>
                              {day.isToday && <span className="weekday-dot" />}
                            </div>
                          ))}
                        </div>

                        <div className="section-title-bar" style={{ marginTop: '1.25rem' }}>
                          <h4>Appointments</h4>
                          <span className="badge-count">{appointments.length}</span>
                        </div>

                        {loadingAppointments && appointments.length === 0 ? (
                          <div className="panel-loading"><div className="spinner-mini" /></div>
                        ) : appointments.length === 0 ? (
                          <div className="panel-empty-state">
                            <Calendar size={24} />
                            <p>No appointments today</p>
                          </div>
                        ) : (
                          <div className="panel-list-items">
                            {appointments.map(appt => (
                              <div key={appt.id} className="panel-list-card">
                                <div className="card-top">
                                  <div className="appt-patient-profile">
                                    <div className="avatar-mini">
                                      {(appt.individuals?.[0]?.name || 'P').charAt(0).toUpperCase()}
                                    </div>
                                    <div className="appt-patient-info">
                                      <span className="appt-patient-name">{appt.individuals?.[0]?.name || 'Patient'}</span>
                                      <span className="appt-patient-type">{appt.status === 'confirmed' ? 'Emergency' : 'Check-up'}</span>
                                    </div>
                                  </div>
                                  <div className="appt-right-group">
                                    <span className="appt-time">{formatApptTime(appt.slot_time)}</span>
                                    <span className={`status-pill ${appt.status}`}>
                                      {appt.status === 'completed' ? <CheckCircle2 size={16} className="status-check" /> : <Clock size={16} className="status-clock" />}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="right-panel-section top-half" style={{ flex: 1, borderBottom: 'none' }}>
                        <div className="section-title-bar">
                          <h4>Recent Lab Reports</h4>
                          <span className="badge-count">{recentLabReports.length}</span>
                        </div>

                        {loadingReports && recentLabReports.length === 0 ? (
                          <div className="panel-loading"><div className="spinner-mini" /></div>
                        ) : recentLabReports.length === 0 ? (
                          <div className="panel-empty-state">
                            <FileText size={24} />
                            <p>No reports received</p>
                          </div>
                        ) : (
                          <div className="panel-list-items">
                            {recentLabReports.map(report => (
                              <div key={report.id} className="panel-list-card report-card-panel" style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div className="report-card-details" style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  <span className="report-patient" style={{ fontWeight: 700, fontSize: '0.85rem' }}>{report.patient_name}</span>
                                  <span className="report-date" style={{ fontSize: '0.75rem', color: '#64748b' }}>{new Date(report.updated_at).toLocaleDateString()}</span>
                                </div>
                                <a 
                                  className="report-action-btn"
                                  href={`${window.location.origin}/app/report/${report.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ textDecoration: 'none', textAlign: 'center', display: 'inline-block' }}
                                >
                                  View
                                </a>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Collapsed right panel strip */}
                {isRightPanelCollapsed && (
                  <button className="expand-right-panel-strip" onClick={() => setIsRightPanelCollapsed(false)}>
                    <PanelRightOpen size={18} />
                    <span>Open Care Center</span>
                  </button>
                )}
              </div>
            )}

            {activeTab === 'create-prescription' && (
              <CreatePrescription
                doctorInfo={doctorInfo}
                onComplete={handlePrescriptionComplete}
                preSelectedPatient={selectedPatient}
                editPrescriptionData={editPrescription}
                hasPharmacy={clinicCapabilities.has_pharmacy}
                hasLab={clinicCapabilities.has_lab}
                onRefreshProfile={fetchDoctorInfo}
                onToggleMenu={() => setMobileMenuOpen(!mobileMenuOpen)}
                onGoToSearch={(phone) => {
                  setPendingSearchTerm(phone);
                  setActiveTab('search-patient');
                }}
              />
            )}

            {activeTab === 'search-patient' && (
              <SearchPatient
                doctorInfo={doctorInfo}
                onSelectPatient={handleSelectPatient}
                initialSearch={pendingSearchTerm}
              />
            )}

            {activeTab === 'appointments' && (
              <div className="appointments-section">
                {/* Date Navigator */}
                <div className="appt-date-nav">
                  <button className="appt-nav-arrow" onClick={() => shiftDate(-1)}>
                    <ChevronLeft size={20} />
                  </button>
                  <div className="appt-date-center">
                    <span className="appt-date-label">{formatApptDate(appointmentDate)}</span>
                    {appointmentDate !== new Date().toISOString().split('T')[0] && (
                      <button className="appt-today-pill" onClick={() => setAppointmentDate(new Date().toISOString().split('T')[0])}>
                        Today
                      </button>
                    )}
                  </div>
                  <button className="appt-nav-arrow" onClick={() => shiftDate(1)}>
                    <ChevronRight size={20} />
                  </button>
                </div>

                {/* Appointments List */}
                {loadingAppointments && appointments.length === 0 ? (
                  <div className="empty-state">
                    <div className="spinner"></div>
                    <p>Loading appointments...</p>
                  </div>
                ) : appointments.length === 0 ? (
                  <div className="empty-state">
                    <Calendar size={64} />
                    <h3>No appointments for this day</h3>
                    <p>Appointments booked by patients will appear here</p>
                  </div>
                ) : (
                  <div className="appt-list">
                    {appointments.map(appt => {
                      const statusColor = {
                        pending: 'var(--warning-500)',
                        confirmed: 'var(--primary-500)',
                        completed: 'var(--success-500)',
                        no_show: 'var(--gray-400)'
                      }[appt.status] || 'var(--gray-300)';

                      const individuals = appt.individuals || [];
                      const peopleText = individuals.length <= 2
                        ? individuals.map(i => i.name).join(', ')
                        : `${individuals.length} people`;

                      return (
                        <div key={appt.id} className={`appt-card ${individualPickerAppt?.id === appt.id ? 'active' : ''}`} style={{ borderLeftColor: statusColor }}>

                          <div className="appt-card-body">
                            <div className="appt-info">
                              <div className="appt-time-ref">
                                <span className="appt-time">{formatApptTime(appt.slot_time)}</span>
                                <code className="appt-ref">{appt.booking_ref}</code>
                              </div>
                              <div className="appt-details">
                                {appt.address_line1 && (
                                  <div className="appt-detail-row">
                                    <MapPin size={14} />
                                    <span className="appt-detail-text">
                                      {appt.address_line2 ? `${appt.address_line2}, ` : ''}
                                      {appt.address_line1}
                                      {appt.city ? `, ${appt.city}` : ''}
                                    </span>
                                  </div>
                                )}

                                <div className="appt-detail-row">
                                  <Phone size={14} />
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <a href={`tel:+91${appt.patient_phone}`} className="appt-phone">+91 {appt.patient_phone}</a>
                                    <a 
                                      href={`https://wa.me/91${appt.patient_phone}`} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="whatsapp-link"
                                      title="Chat on WhatsApp"
                                    >
                                      <MessageCircle size={16} />
                                    </a>
                                  </div>
                                </div>

                                <div className="appt-detail-row">
                                  <Users size={14} />
                                  <span>{peopleText}</span>
                                </div>
                              </div>
                            </div>
                            <div className="appt-actions">
                              <span className={`appt-status-badge status-${appt.status}`}>{appt.status.replace('_', ' ')}</span>
                              <div className="appt-btns">
                                {appt.status === 'pending' && (
                                  <>
                                    <button className="btn-primary btn-sm" onClick={() => updateAppointmentStatus(appt.id, 'confirmed')}>Confirm</button>
                                    <button className="btn-outline btn-sm" onClick={() => handleOpenReschedule(appt)}>Reschedule</button>
                                    <button className="btn-danger btn-sm" onClick={() => updateAppointmentStatus(appt.id, 'cancelled')}>Cancel</button>
                                  </>
                                )}
                                {appt.status === 'confirmed' && (
                                  <>
                                    <div className="appt-btn-group">
                                      <button className="btn-primary btn-sm" onClick={() => handleStartCertFromAppointment(appt)}>
                                        Start Certificate{individuals.length > 1 ? ` (${individuals.length})` : ''}
                                      </button>
                                    </div>
                                    <button className="btn-success btn-sm" onClick={() => updateAppointmentStatus(appt.id, 'completed')}>Mark Complete</button>
                                    <button className="btn-outline btn-sm" onClick={() => handleOpenReschedule(appt)}>Reschedule</button>
                                    <button className="btn-secondary btn-sm" onClick={() => updateAppointmentStatus(appt.id, 'no_show')}>No Show</button>
                                  </>
                                )}
                                {appt.status === 'completed' && (
                                  <span className="appt-completed-text"><CheckCircle2 size={16} /> Completed</span>
                                )}
                              </div>
                            </div>
                          </div>
                          {appt.notes && (
                            <div className="appt-notes">
                              <span>📝</span> {appt.notes}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

              </div>
            )}

            {activeTab === 'history' && (
              <PrescriptionHistory
                prescriptions={allPrescriptions}
                certificates={allCertificates}
                onEdit={handleEditPrescription}
                onCancel={handleCancelPrescription}
                initialFilter={historyFilter}
                hasPharmacy={clinicCapabilities.has_pharmacy}
                hasLab={clinicCapabilities.has_lab}
              />
            )}

            {activeTab === 'certificate' && (
              <FitnessCertificate 
                doctor={doctorInfo}
                clinic={{ id: doctorInfo.clinic_id }}
                existingPatient={selectedPatientForCert}
                appointmentId={appointmentForCert?.id}
                individualId={selectedIndividual?.id}
                onClose={() => {
                  setActiveTab(appointmentForCert ? 'appointments' : 'overview');
                  setAppointmentForCert(null);
                  setSelectedIndividual(null);
                }}

                onSuccess={async (cert) => {
                  setViewingCertificate(cert);
                  if (appointmentForCert) {
                    // Don't auto-complete multi-person; doctor uses Mark Complete
                    const individuals = appointmentForCert.individuals || [];
                    if (individuals.length <= 1) {
                      await updateAppointmentStatus(appointmentForCert.id, 'completed');
                    }
                    setAppointmentForCert(null);
                  }
                  setActiveTab('overview');
                  fetchAllCertificates();
                  fetchAppointments(appointmentDate); // Instant refresh for individual tracking
                }}

              />
            )}

            {activeTab === 'profile' && (
              <div className="profile-vertical-layout">
                <aside className="profile-sidebar-nav">
                  <button 
                    className={`profile-nav-btn ${profileSubTab === 'identity' ? 'active' : ''}`}
                    onClick={() => setProfileSubTab('identity')}
                  >
                    Professional Identity
                  </button>
                  <button 
                    className={`profile-nav-btn ${profileSubTab === 'signature' ? 'active' : ''}`}
                    onClick={() => setProfileSubTab('signature')}
                  >
                    Digital Signature
                  </button>
                  <button 
                    className={`profile-nav-btn ${profileSubTab === 'schedule' ? 'active' : ''}`}
                    onClick={() => setProfileSubTab('schedule')}
                  >
                    Practice & Schedule
                  </button>
                  <button 
                    className={`profile-nav-btn ${profileSubTab === 'favorites' ? 'active' : ''}`}
                    onClick={() => setProfileSubTab('favorites')}
                  >
                    Personal Preferences & Presets
                  </button>
                </aside>

                <div className="profile-content-area">
                  {profileSubTab === 'identity' && (
                    <div className="content-card profile-card">
                    <div className="profile-card-header">
                      <div>
                        <h3>Professional Identity</h3>
                        <p>Your basic information and credentials.</p>
                      </div>
                      {!isEditingProfile && (
                        <button className="btn-edit-profile" onClick={() => setIsEditingProfile(true)}>
                          Edit Profile
                        </button>
                      )}
                    </div>
                    
                    {!isEditingProfile ? (
                      <div className="profile-readonly-view">
                        <div className="profile-readonly-grid">
                          <div className="readonly-item">
                            <span className="readonly-label">Full Name</span>
                            <span className="readonly-value">{profileData.name || '-'}</span>
                          </div>
                          <div className="readonly-item">
                            <span className="readonly-label">Phone Number</span>
                            <span className="readonly-value">{profileData.phone || '-'}</span>
                          </div>
                          <div className="readonly-item">
                            <span className="readonly-label">Login Email</span>
                            <span className="readonly-value">{profileData.email || '-'}</span>
                          </div>
                          <div className="readonly-item">
                            <span className="readonly-label">Qualification(s)</span>
                            <span className="readonly-value">{profileData.qualification || '-'}</span>
                          </div>
                          <div className="readonly-item">
                            <span className="readonly-label">Specialization</span>
                            <span className="readonly-value">{profileData.specialization || '-'}</span>
                          </div>
                          <div className="readonly-item">
                            <span className="readonly-label">Registration Number</span>
                            <span className="readonly-value">{profileData.registration_number || '-'}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <form className="profile-form" onSubmit={handleSaveProfile}>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Full Name</label>
                        <input
                          type="text"
                          value={profileData.name}
                          onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                          required
                          placeholder="Dr. John Doe"
                        />
                      </div>
                      <div className="form-group">
                        <label>Phone Number</label>
                        <input
                          type="tel"
                          value={profileData.phone}
                          onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                          placeholder="+91 9876543210"
                        />
                      </div>
                      <div className="form-group">
                        <label>Login Email</label>
                        <input
                          type="email"
                          value={profileData.email}
                          disabled
                          className="disabled-input"
                          title="Used for login, can't be changed here"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Qualification(s)</label>
                        <input
                          type="text"
                          value={profileData.qualification}
                          onChange={(e) => setProfileData({ ...profileData, qualification: e.target.value })}
                          placeholder="e.g. MBBS, MD"
                        />
                      </div>
                      <div className="form-group">
                        <label>Specialization</label>
                        <input
                          type="text"
                          value={profileData.specialization}
                          onChange={(e) => setProfileData({ ...profileData, specialization: e.target.value })}
                          placeholder="e.g. General Physician"
                        />
                      </div>
                      <div className="form-group">
                        <label>Registration Number</label>
                        <input
                          type="text"
                          value={profileData.registration_number}
                          onChange={(e) => setProfileData({ ...profileData, registration_number: e.target.value })}
                          placeholder="e.g. MCI-12345"
                        />
                      </div>
                    </div>

                    <div className="profile-form-actions">
                      <button type="button" className="btn-cancel-profile" onClick={() => setIsEditingProfile(false)} disabled={isSavingProfile}>
                        Cancel
                      </button>
                      <button type="submit" className="btn-save-profile" disabled={isSavingProfile}>
                        {isSavingProfile ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </form>
                    )}
                  </div>
                  )}

                  {profileSubTab === 'signature' && (
                  <div className="content-card profile-card">
                    <div className="profile-card-header">
                      <h3>Digital Signature</h3>
                      <p>Used to legally sign your generated digital prescriptions.</p>
                    </div>

                    <div className="signature-manager">
                      {doctorInfo?.signature_url ? (
                        <div className="current-signature-section" style={{ marginBottom: '2rem', padding: '1rem', background: '#f8fafc', borderRadius: '0.5rem', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div>
                            <span style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>Current Signature</span>
                            <img src={doctorInfo.signature_url} alt="Current Signature" style={{ maxHeight: '80px', background: 'white', padding: '0.5rem', borderRadius: '0.25rem', border: '1px solid #cbd5e1' }} />
                          </div>
                          <button onClick={handleRemoveSignature} style={{ padding: '0.5rem 1rem', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: '0.375rem', fontWeight: '500', cursor: 'pointer' }}>
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div style={{ marginBottom: '2rem', padding: '1rem', background: '#fffbeb', borderRadius: '0.5rem', border: '1px solid #fde68a', color: '#b45309', fontSize: '0.9rem' }}>
                          You have not uploaded a digital signature yet.
                        </div>
                      )}

                      <div className="signature-input-area">
                        <div className="signature-tabs" style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', marginBottom: '1.5rem' }}>
                          <button 
                            className={`sig-tab ${signatureMode === 'draw' ? 'active' : ''}`}
                            onClick={() => setSignatureMode('draw')}
                            style={{ padding: '0.5rem 1rem', background: 'none', border: 'none', borderBottom: signatureMode === 'draw' ? '2px solid #4f46e5' : '2px solid transparent', color: signatureMode === 'draw' ? '#4f46e5' : '#6b7280', fontWeight: '500', cursor: 'pointer' }}
                          >
                            Draw Signature
                          </button>
                          <button 
                            className={`sig-tab ${signatureMode === 'upload' ? 'active' : ''}`}
                            onClick={() => setSignatureMode('upload')}
                            style={{ padding: '0.5rem 1rem', background: 'none', border: 'none', borderBottom: signatureMode === 'upload' ? '2px solid #4f46e5' : '2px solid transparent', color: signatureMode === 'upload' ? '#4f46e5' : '#6b7280', fontWeight: '500', cursor: 'pointer' }}
                          >
                            Upload Image
                          </button>
                        </div>

                        {signatureMode === 'draw' && (
                          <div className="draw-signature-container" ref={sigContainerRef}>
                            <canvas
                              ref={canvasRef}
                              width={canvasWidth}
                              height={200}
                              className="signature-canvas"
                              onMouseDown={startDrawing}
                              onMouseMove={draw}
                              onMouseUp={stopDrawing}
                              onMouseLeave={stopDrawing}
                              onTouchStart={startDrawing}
                              onTouchMove={draw}
                              onTouchEnd={stopDrawing}
                              style={{ border: '2px dashed #cbd5e1', borderRadius: '0.5rem', background: '#f8fafc', cursor: 'crosshair', touchAction: 'none', width: '100%' }}
                            />
                            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                              <button type="button" onClick={clearSignaturePad} style={{ padding: '0.5rem 1rem', background: 'white', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontWeight: '500', cursor: 'pointer' }}>
                                Clear Pad
                              </button>
                              <button type="button" onClick={handleSaveSignature} disabled={isSavingSignature} className="btn-save-profile">
                                {isSavingSignature ? 'Saving...' : 'Save Drawn Signature'}
                              </button>
                            </div>
                          </div>
                        )}

                        {signatureMode === 'upload' && (
                          <div className="upload-signature-container" style={{ padding: '2rem', border: '2px dashed #cbd5e1', borderRadius: '0.5rem', background: '#f8fafc', textAlign: 'center' }}>
                            <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                              {signatureFile ? `Selected: ${signatureFile.name}` : 'Select a .png or .jpg image of your signature with a clear background.'}
                            </p>
                            <input
                              type="file"
                              id="sig-upload"
                              style={{ display: 'none' }}
                              accept=".png,.jpg,.jpeg"
                              onChange={(e) => setSignatureFile(e.target.files[0])}
                            />
                            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                              <label htmlFor="sig-upload" style={{ background: 'white', border: '1px solid #cbd5e1', padding: '0.5rem 1rem', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: '500', color: '#334155', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                                Browse Image
                              </label>
                              {signatureFile && (
                                <button type="button" onClick={handleSaveSignature} disabled={isSavingSignature} className="btn-save-profile">
                                  {isSavingSignature ? 'Uploading...' : 'Upload Signature'}
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {profileSubTab === 'schedule' && (
                  <div className="content-card profile-card">
                    <div className="profile-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <h3>Practice & Schedule</h3>
                        <p>Configure your clinic hours and booking preferences.</p>
                      </div>
                      {!isEditingProfile && (
                        <button className="btn-edit-profile" onClick={() => setIsEditingProfile(true)} style={{ background: '#f8fafc', color: '#4f46e5', border: '1px solid #e2e8f0', padding: '0.5rem 1rem', borderRadius: '0.375rem', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                          Edit Schedule
                        </button>
                      )}
                    </div>

                    {!isEditingProfile ? (
                      <div className="profile-readonly-view">
                        <div className="profile-readonly-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '2rem' }}>
                          <div className="readonly-item" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <span className="readonly-label" style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Available Days</span>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                              {profileData.available_days && profileData.available_days.length > 0 ? (
                                profileData.available_days.map(day => (
                                  <span key={day} className="day-pill selected readonly">{day}</span>
                                ))
                              ) : (
                                <span style={{ fontSize: '0.9rem', color: '#9ca3af', fontStyle: 'italic' }}>No days configured.</span>
                              )}
                            </div>
                          </div>

                          <div className="readonly-item" style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            <span className="readonly-label" style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Visible Working Hours</span>
                            <span className="readonly-value" style={{ fontSize: '1rem', color: '#111827', fontWeight: '500' }}>{profileData.working_hours || '-'}</span>
                            <span style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: '0.25rem' }}>* Printed on all Rx headers.</span>
                          </div>
                          <div className="readonly-item" style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <span className="readonly-label" style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Avg. Consultation Time</span>
                              <span style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem', background: '#fef3c7', color: '#d97706', borderRadius: '1rem', fontWeight: '600', textTransform: 'uppercase' }}>Coming in Phase 2B</span>
                            </div>
                            <span className="readonly-value" style={{ fontSize: '1rem', color: '#9ca3af', fontWeight: '500' }}>15 Mins</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <form className="profile-form" onSubmit={handleSaveProfile}>
                        <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
                          <div className="form-group">
                            <label>Available Days <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 'normal', marginLeft: '0.5rem' }}>(Phase 2B Scheduling)</span></label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.25rem' }}>
                              {WEEKDAYS.map(day => {
                                const isSelected = (profileData.available_days || []).includes(day);
                                return (
                                  <button
                                    key={day}
                                    type="button"
                                    className={`day-pill ${isSelected ? 'selected' : ''}`}
                                    onClick={() => toggleDay(day)}
                                  >
                                    {day}
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          <div className="form-group" style={{ marginTop: '0.5rem' }}>
                            <label>Appointment Booking Hours</label>
                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '0.25rem' }}>
                              <div style={{ flex: 1 }}>
                                <span style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>Start Time</span>
                                <input
                                  type="time"
                                  value={profileData.startTime}
                                  onChange={(e) => setProfileData({ ...profileData, startTime: e.target.value })}
                                />
                              </div>
                              <div style={{ fontSize: '1.2rem', color: '#9ca3af', marginTop: '1.2rem' }}>→</div>
                              <div style={{ flex: 1 }}>
                                <span style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>End Time</span>
                                <input
                                  type="time"
                                  value={profileData.endTime}
                                  onChange={(e) => setProfileData({ ...profileData, endTime: e.target.value })}
                                />
                              </div>
                            </div>
                            <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.5rem' }}>
                              * Updates your public booking slots and Rx header automatically.
                            </p>
                          </div>


                          <div className="form-group">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                              <label style={{ margin: 0 }}>Avg. Consultation Time</label>
                              <span style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem', background: '#fef3c7', color: '#d97706', borderRadius: '1rem', fontWeight: '600', textTransform: 'uppercase' }}>Phase 2B</span>
                            </div>
                            <input
                              type="text"
                              value="15 Mins"
                              disabled
                              className="disabled-input"
                              title="Appointment settings will be available in the next phase."
                            />
                          </div>
                        </div>

                        <div className="profile-form-actions" style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', paddingTop: '2rem', borderTop: '1px solid #e5e7eb', marginTop: '1rem' }}>
                          <button type="button" className="btn-cancel-profile" onClick={() => setIsEditingProfile(false)} disabled={isSavingProfile} style={{ background: 'transparent', color: '#4b5563', border: '1px solid #d1d5db', padding: '0.75rem 1.5rem', borderRadius: '0.375rem', fontWeight: '500', cursor: 'pointer' }}>
                            Cancel
                          </button>
                          <button type="submit" className="btn-save-profile" disabled={isSavingProfile}>
                            {isSavingProfile ? 'Saving...' : 'Save Schedule'}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}

                {profileSubTab === 'favorites' && (
                  <div className="content-card profile-card">
                    <div className="profile-card-header">
                      <div>
                        <h3>Personal Preferences & Presets</h3>
                        <p>Curate your list of favorite medicines for fast 1-click prescribing.</p>
                      </div>
                      {!isEditingProfile && (
                        <button className="btn-edit-profile" onClick={() => setIsEditingProfile(true)}>
                          Edit Favorites
                        </button>
                      )}
                    </div>

                    {!isEditingProfile ? (
                      <div className="profile-readonly-view">
                        <div className="profile-readonly-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '2rem' }}>
                          <div className="readonly-item" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <span className="readonly-label" style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Favorite Medicines</span>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                              {profileData.favorite_medicines && profileData.favorite_medicines.length > 0 ? (
                                profileData.favorite_medicines.map((fav, idx) => (
                                  <span key={idx} className="day-pill selected readonly" style={{ background: '#f8fafc', color: '#4f46e5', border: '1px solid #e2e8f0' }}>{fav.name}</span>
                                ))
                              ) : (
                                <span style={{ fontSize: '0.9rem', color: '#9ca3af', fontStyle: 'italic' }}>No favorite medicines curated yet.</span>
                              )}
                            </div>
                          </div>

                          <div className="readonly-item" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <span className="readonly-label" style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Prescription Templates</span>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                              {profileData.prescription_templates && profileData.prescription_templates.length > 0 ? (
                                profileData.prescription_templates.map((tpl, idx) => (
                                  <span key={idx} className="day-pill selected readonly" style={{ background: '#fffbeb', color: '#b45309', border: '1px solid #fde68a' }}>{tpl.template_name}</span>
                                ))
                              ) : (
                                <span style={{ fontSize: '0.9rem', color: '#9ca3af', fontStyle: 'italic' }}>No quick-fill templates saved yet. Create them when writing new prescriptions.</span>
                              )}
                            </div>
                          </div>

                          <div className="readonly-item" style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            <span className="readonly-label" style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Default Follow-up</span>
                            <span className="readonly-value" style={{ fontSize: '1rem', color: '#111827', fontWeight: '500' }}>
                              {profileData.default_followup_days === 0 ? 'None' : `${profileData.default_followup_days} Days`}
                            </span>
                          </div>
                   </div>
                 </div>
                    ) : (
                      <form className="profile-form" onSubmit={handleSaveProfile} style={{ position: 'relative' }}>
                        <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
                          <div className="form-group" style={{ position: 'relative' }}>
                            <label>Add Favorite Medicine Formulation</label>
                            <input
                              type="text"
                              value={medicineSearchQuery}
                              onChange={(e) => handleMedicineSearch(e.target.value)}
                              placeholder="Search API for drug name..."
                              autoComplete="off"
                            />
                            {isSearchingMedicine && <span style={{ position: 'absolute', right: '1rem', top: '2.5rem', fontSize: '0.8rem', color: '#6b7280' }}>Searching...</span>}
                            
                            {(medicineSearchResults.length > 0 || (medicineSearchQuery.trim().length > 0 && !isSearchingMedicine)) && (
                              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '0.375rem', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', zIndex: 50, maxHeight: '200px', overflowY: 'auto', marginTop: '0.25rem' }}>
                                {medicineSearchResults.map((med, idx) => (
                                  <div 
                                    key={idx} 
                                    onClick={() => addFavoriteMedicine(med)}
                                    style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', background: 'white', fontSize: '0.9rem' }}
                                    onMouseOver={(e) => e.currentTarget.style.background = '#f8fafc'}
                                    onMouseOut={(e) => e.currentTarget.style.background = 'white'}
                                  >
                                    <div style={{ fontWeight: '500', color: '#0f172a' }}>{med.name}</div>
                                    {med.composition && <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{med.composition}</div>}
                                  </div>
                                ))}

                                {medicineSearchQuery.trim().length > 0 && (
                                  <div 
                                    onClick={() => addFavoriteMedicine({ name: medicineSearchQuery.trim(), composition: '' })}
                                    style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', background: '#f8fafc', borderTop: '1px solid #e2e8f0', fontSize: '0.9rem' }}
                                    onMouseOver={(e) => e.currentTarget.style.background = '#f1f5f9'}
                                    onMouseOut={(e) => e.currentTarget.style.background = '#f8fafc'}
                                  >
                                    <div style={{ fontWeight: '500', color: '#4f46e5', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                      <FilePlus size={16} /> Add Custom: "{medicineSearchQuery.trim()}"
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          
                          <div className="form-group" style={{ marginTop: '0.5rem' }}>
                            <label>Your Favorites List <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 'normal', marginLeft: '0.5rem' }}>(Clicks to remove)</span></label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.25rem' }}>
                              {profileData.favorite_medicines && profileData.favorite_medicines.length > 0 ? (
                                profileData.favorite_medicines.map((fav, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    onClick={() => removeFavoriteMedicine(fav.name)}
                                    style={{ padding: '0.4rem 0.75rem', background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: '0.375rem', fontSize: '0.85rem', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                                    onMouseOver={(e) => e.currentTarget.style.background = '#fecaca'}
                                    onMouseOut={(e) => e.currentTarget.style.background = '#fee2e2'}
                                  >
                                    {fav.name} <X size={14} />
                                  </button>
                                ))
                              ) : (
                                <span style={{ fontSize: '0.9rem', color: '#9ca3af', fontStyle: 'italic' }}>No favorites added yet.</span>
                              )}
                            </div>
                          </div>

                          <div className="form-group" style={{ marginTop: '0.5rem' }}>
                            <label>Prescription Templates <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 'normal', marginLeft: '0.5rem' }}>(Clicks to remove)</span></label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.25rem' }}>
                              {profileData.prescription_templates && profileData.prescription_templates.length > 0 ? (
                                profileData.prescription_templates.map((tpl, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    onClick={() => removeTemplate(tpl.template_name)}
                                    style={{ padding: '0.4rem 0.75rem', background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: '0.375rem', fontSize: '0.85rem', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                                    onMouseOver={(e) => e.currentTarget.style.background = '#fecaca'}
                                    onMouseOut={(e) => e.currentTarget.style.background = '#fee2e2'}
                                  >
                                    {tpl.template_name} <X size={14} />
                                  </button>
                                ))
                              ) : (
                                <span style={{ fontSize: '0.9rem', color: '#9ca3af', fontStyle: 'italic' }}>Templates are created from the Prescription Page.</span>
                              )}
                            </div>
                          </div>
                          <div className="form-group" style={{ marginTop: '0.5rem', borderTop: '1px solid #f1f5f9', paddingTop: '1.5rem' }}>
                            <label style={{ color: '#4f46e5', fontWeight: '600', marginBottom: '1rem', display: 'block' }}>Prescription Defaults</label>
                            
                            <div className="form-group">
                              <label>Default Follow-up Duration</label>
                              <select 
                                value={profileData.default_followup_days}
                                onChange={(e) => setProfileData({ ...profileData, default_followup_days: e.target.value })}
                                style={{ width: '100%', padding: '0.75rem', borderRadius: '0.375rem', border: '1px solid #d1d5db' }}
                              >
                                <option value={0}>None</option>
                                <option value={3}>3 Days</option>
                                <option value={7}>7 Days (1 Week)</option>
                                <option value={10}>10 Days</option>
                                <option value={14}>14 Days (2 Weeks)</option>
                                <option value={21}>21 Days (3 Weeks)</option>
                                <option value={30}>30 Days (1 Month)</option>
                              </select>
                              <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.5rem' }}>* Automatically pre-calculates the follow-up date on new prescriptions.</p>
                            </div>
                          </div>
                        </div>

                        <div className="profile-form-actions">
                          <button type="button" className="btn-cancel-profile" onClick={() => setIsEditingProfile(false)} disabled={isSavingProfile}>
                            Cancel
                          </button>
                          <button type="submit" className="btn-save-profile" disabled={isSavingProfile}>
                            {isSavingProfile ? 'Saving...' : 'Save Preferences'}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}

                </div>
              </div>
            )}
          </div>
        </main>

      {viewingCertificate && (
        <CertificateViewer 
          certificate={viewingCertificate}
          onClose={() => setViewingCertificate(null)}
        />
      )}

      {selectedPrescriptionForView && (
        <PrescriptionViewer 
          prescription={selectedPrescriptionForView}
          mode={viewMode}
          onClose={() => setSelectedPrescriptionForView(null)}
        />
      )}

        {/* Mobile Bottom Navigation */}
        <div className="mobile-bottom-nav">
          <button 
            className={`nav-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <LayoutDashboard size={20} />
            <span>Home</span>
          </button>
          <button 
            className={`nav-tab ${activeTab === 'create-prescription' ? 'active' : ''}`}
            onClick={() => setActiveTab('create-prescription')}
          >
            <FilePlus size={20} />
            <span>New Rx</span>
          </button>
          <button 
            className={`nav-tab ${activeTab === 'appointments' ? 'active' : ''}`}
            onClick={() => setActiveTab('appointments')}
          >
            <Calendar size={20} />
            <span>Appts</span>
          </button>
          <button 
            className={`nav-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            <History size={20} />
            <span>History</span>
          </button>
          <button 
            className={`nav-tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            <User size={20} />
            <span>Profile</span>
          </button>
        </div>
      </div>
      {/* Reschedule Modal */}
      {rescheduleItem && (
        <div className="reschedule-modal-overlay" onClick={() => setRescheduleItem(null)}>
          <div className="reschedule-modal" onClick={e => e.stopPropagation()}>
            <div className="reschedule-header">
              <h3>Reschedule Appointment</h3>
              <button className="close-btn" onClick={() => setRescheduleItem(null)}><X size={20} /></button>
            </div>
            <div className="reschedule-body">
              <div className="reschedule-info">
                <p>Rescheduling appointment for <strong>{rescheduleItem.individuals?.[0]?.name || 'Patient'}</strong></p>
                <span>Current: {rescheduleItem.preferred_date} • {rescheduleItem.slot_time}</span>
              </div>
              
              <div className="reschedule-form">
                <div className="form-group">
                  <label>Select New Date</label>
                  <input 
                    type="date" 
                    min={new Date().toISOString().split('T')[0]}
                    value={rescheduleDate}
                    onChange={(e) => {
                      setRescheduleDate(e.target.value);
                      fetchRescheduleBookedSlots(e.target.value);
                    }}
                  />
                </div>

                <div className="reschedule-slots">
                  <label>Available Slots</label>
                  <div className="slots-grid-mini">
                    {generateDashboardTimeSlots().map(slot => {
                      const isBooked = rescheduleBookedSlots.includes(slot);
                      const isSelected = selectedRescheduleSlot === slot;
                      return (
                        <button
                          key={slot}
                          className={`slot-pill ${isBooked ? 'booked' : ''} ${isSelected ? 'selected' : ''}`}
                          disabled={isBooked}
                          onClick={() => setSelectedRescheduleSlot(slot)}
                        >
                          {slot}
                          {isBooked && <span className="booked-dot" />}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="reschedule-actions" style={{ marginTop: '2rem' }}>
                  <button 
                    className="btn-primary full-width"
                    disabled={!selectedRescheduleSlot || isUpdatingReschedule}
                    onClick={handleUpdateSchedule}
                  >
                    {isUpdatingReschedule ? 'Updating...' : 'Confirm Reschedule'}
                  </button>
                  <button 
                    className="btn-link full-width"
                    style={{ marginTop: '0.5rem' }}
                    onClick={() => setRescheduleItem(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Global Singleton Picker - MOVED TO ROOT FOR MAXIMUM VISIBILITY */}
      {individualPickerAppt && (
        <>
          <div className="picker-backdrop" onClick={() => setIndividualPickerAppt(null)} />
          <div className="individual-picker">
            <div className="mobile-handle" />
            <div className="picker-header">Select Person</div>
            {(individualPickerAppt.individuals || []).map((ind, idx) => {
              const nameA = ind.name?.toLowerCase().trim();
              const isDone = issuedCertificates.some(c => {
                const nameB = c.patient_name?.toLowerCase().trim();
                return nameA && nameB && ((c.appointment_id === individualPickerAppt.id && nameB === nameA) ||
                       (!c.appointment_id && nameB === nameA));
              });
              
              return (
                <button 
                  key={idx} 
                  className={`picker-item ${isDone ? 'completed' : ''}`} 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!isDone) selectIndividualForCert(individualPickerAppt, ind);
                  }}
                  disabled={isDone}
                >
                  <span className="picker-num">{idx + 1}</span>
                  <div className="picker-info">
                    <span className="picker-name">
                      {ind.name}
                      {isDone && <CheckCircle2 size={14} className="done-icon" />}
                    </span>
                    <span className="picker-meta">{ind.age}y • {ind.gender}</span>
                  </div>
                  {isDone && <span className="done-badge">Issued</span>}
                </button>
              );
            })}
            <div className="picker-footer">
              <button 
                className="picker-item add-new-item"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  selectIndividualForCert(individualPickerAppt, { name: '', age: '', gender: 'Male' });
                }}
              >
                <UserPlus size={14} className="add-icon" />
                <div className="picker-info">
                  <span className="picker-name">Add Person (Not in list)</span>
                  <span className="picker-meta">Starts with blank name & age</span>
                </div>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Premium Start New Prescription Phone Modal */}
      {showPhoneModal && (
        <div className="premium-modal-overlay">
          <div className="premium-modal-card">
            <div className="modal-header">
              <h3>Start New Prescription</h3>
              <button 
                className="modal-close-btn" 
                onClick={() => { setShowPhoneModal(false); setModalPhone(''); }}
                title="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-subtitle">Enter the patient's registered phone number to load their profile and start consultation.</p>
              <div className="modal-input-wrapper">
                <span className="phone-prefix">+91</span>
                <input 
                  type="tel" 
                  placeholder="Enter 10-digit phone number" 
                  value={modalPhone}
                  onChange={(e) => setModalPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  className="premium-modal-input"
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-modal-cancel" 
                onClick={() => { setShowPhoneModal(false); setModalPhone(''); }}
              >
                Cancel
              </button>
              <button 
                className="btn-modal-proceed"
                onClick={handleProceedWithPhone}
                disabled={modalPhone.length < 10 || modalSearching}
              >
                {modalSearching ? 'Searching...' : 'Proceed'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLeaveTabModal && (
        <div className="custom-confirm-overlay no-print">
          <div className="custom-confirm-modal">
            <div className="confirm-modal-header">
              <AlertTriangle size={24} className="confirm-warning-icon" />
              <h3>Discard Prescription?</h3>
            </div>
            <div className="confirm-modal-body">
              <p>Your prescription draft is not complete yet. Are you sure you want to leave this page? Any unsaved changes will be lost.</p>
            </div>
            <div className="confirm-modal-footer">
              <button className="btn-confirm-cancel" onClick={() => { setShowLeaveTabModal(false); setPendingTab(null); }}>
                Keep Editing
              </button>
              <button className="btn-confirm-discard" onClick={() => {
                setShowLeaveTabModal(false);
                if (pendingTab) {
                  setSearchParams({ tab: pendingTab });
                  setPendingTab(null);
                }
              }}>
                Discard & Leave
              </button>
            </div>
          </div>
        </div>
      )}
    </>

  );
}

export default DoctorDashboard;